import {
    AdvancedMarker,
    APIProvider,
    Map,
    useMap,
    useMapsLibrary
} from "@vis.gl/react-google-maps";
import {useCallback, useEffect, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import DashCard from "../../../../../components/dash-card/dash-card.jsx";
import corporationService from "../../../../../services/web/corporation-service.js";
import MapDarkOverlay from "../../../../../components/map-dark-overlay/map-dark-overlay.jsx";

const DEFAULT_CENTER = {lat: -23.0, lng: -46.0};
const SELECTION_TIMEOUT_MS = 10000;

function MapPassagesContent({places}) {
    const map = useMap();
    const coreLib = useMapsLibrary("core");
    const previousPlacesSignatureRef = useRef(null);
    const latestPlacesRef = useRef(places);

    const [selectedPlaceId, setSelectedPlaceId] = useState(null);

    const getPlaceId = useCallback(place =>
        place.id != null
            ? `id:${place.id}`
            : `coordinates:${place.latitude},${place.longitude}`, []);

    const selectedPlace = places.find(
        place => getPlaceId(place) === selectedPlaceId
    ) ?? null;

    const fitAllPlaces = useCallback(currentPlaces => {
        if (!map || !coreLib || currentPlaces.length === 0) return;

        if (currentPlaces.length === 1) {
            map.setCenter({
                lat: currentPlaces[0].latitude,
                lng: currentPlaces[0].longitude,
            });

            map.setZoom(14);
            return;
        }

        const bounds = new coreLib.LatLngBounds();

        currentPlaces.forEach(place => {
            bounds.extend({
                lat: place.latitude,
                lng: place.longitude,
            });
        });

        map.fitBounds(bounds, {
            top: 70,
            right: 10,
            bottom: 10,
            left: 10,
        });
    }, [map, coreLib]);

    useEffect(() => {
        latestPlacesRef.current = places;
    }, [places]);

    useEffect(() => {
        if (!map || !coreLib || places.length === 0) return;

        const placesSignature = JSON.stringify(
            [...new Set(places.map(getPlaceId))].sort()
        );

        if (previousPlacesSignatureRef.current === placesSignature) return;

        previousPlacesSignatureRef.current = placesSignature;

        fitAllPlaces(places);
    }, [map, coreLib, fitAllPlaces, getPlaceId, places]);

    useEffect(() => {
        if (!map || places.length === 0) {
            setSelectedPlaceId(null);
            return;
        }

        const currentPlaceExists = places.some(
            place => getPlaceId(place) === selectedPlaceId
        );

        if (!currentPlaceExists) {
            const firstPlace = places[0];

            setSelectedPlaceId(getPlaceId(firstPlace));

            map.panTo({
                lat: firstPlace.latitude,
                lng: firstPlace.longitude,
            });
        }

        const intervalId = window.setInterval(() => {
            const currentPlaces = latestPlacesRef.current;

            if (currentPlaces.length === 0) {
                setSelectedPlaceId(null);
                return;
            }

            setSelectedPlaceId(currentSelectedPlaceId => {
                const currentIndex = currentPlaces.findIndex(
                    place => getPlaceId(place) === currentSelectedPlaceId
                );

                const nextIndex =
                    currentIndex === -1
                        ? 0
                        : (currentIndex + 1) % currentPlaces.length;

                const nextPlace = currentPlaces[nextIndex];

                map.panTo({
                    lat: nextPlace.latitude,
                    lng: nextPlace.longitude,
                });

                return getPlaceId(nextPlace);
            });
        }, SELECTION_TIMEOUT_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [getPlaceId, map, places.length]);

    const handleMarkerClick = (place) => {
        setSelectedPlaceId(getPlaceId(place));

        map?.panTo({
            lat: place.latitude,
            lng: place.longitude,
        });
    };

    return (
        <div className="relative h-full w-full overflow-hidden rounded-xl">
            <Map
                reuseMaps
                mapId="PASSAGES_MAP_ID"
                defaultZoom={12}
                defaultCenter={
                    places[0]
                        ? {
                            lat: places[0].latitude,
                            lng: places[0].longitude,
                        }
                        : DEFAULT_CENTER
                }
                disableDefaultUI
                keyboardShortcuts={false}
                streetViewControl={false}
                mapTypeControl={false}
                fullscreenControl={false}
                zoomControl={false}
                cameraControl={false}
                gestureHandling="cooperative"
                style={{
                    height: "100%",
                    width: "100%",
                }}
            >
                <MapDarkOverlay />
                {places.map(place => {
                    const placeId = getPlaceId(place);
                    const isSelected = selectedPlaceId === placeId;

                    return (
                        <AdvancedMarker
                            key={placeId}
                            position={{
                                lat: place.latitude,
                                lng: place.longitude,
                            }}
                            title={place.description}
                            zIndex={isSelected ? 100 : 1}
                            onClick={() => handleMarkerClick(place)}
                        >
                            <div
                                className={`
                                    relative flex items-center justify-center rounded-full border-2 border-[#4F7CFF] bg-[#0E4A85]/70 shadow-[0_0_10px_rgba(28,157,211,0.65)] transition-all duration-200
                                    ${isSelected ? "h-14 w-14 scale-110" : "h-12 w-12 hover:scale-105"}
                                `}
                            >
                                <div className="absolute inset-1 rounded-full bg-gradient-to-b from-[#105391] to-[#1C9DD3] shadow-inner"
                                />

                                <span className="relative z-10 text-sm font-bold text-[#E8E8E8]">
                                    {(place.qty ?? 0).toLocaleString("pt-BR")}
                                </span>
                            </div>
                        </AdvancedMarker>
                    );
                })}
            </Map>

            {/*<div className="pointer-events-none absolute inset-0 z-10 bg-black/40" />*/}

            {selectedPlace && (
                <div className="pointer-events-none absolute left-0 right-0 top-0 z-20 px-4 pb-10 pt-4">
                    <p className="pb-1 text-[#F4FFFF] drop-shadow-[0_2px_3px_rgba(0,0,0,)0.9]">
                        Nº de passagem - Top 5 pontos
                    </p>
                    <p className="text-base font-semibold leading-tight text-[#F4FFFF] drop-shadow-[0_2px_3px_rgba(0,0,0,)0.9]">
                        {selectedPlace.description}
                    </p>
                </div>
            )}
        </div>
    );
}

export default function MapPassagesCard({capturePlaces}) {
    const places = (capturePlaces?.topCapturePlaces ?? [])
        .filter(
            place =>
                Number.isFinite(Number(place.latitude)) &&
                Number.isFinite(Number(place.longitude))
        )
        .map(place => ({
            ...place,
            latitude: Number(place.latitude),
            longitude: Number(place.longitude),
        }));

    const {data: corporation} = useQuery({
        queryKey: ["corporation"],
        queryFn: () => corporationService.entity(),
    });

    return (
        <DashCard
            width="100%"
            height="100%"
            className="box-border min-w-0 min-h-0 h-full w-full p-0! flex flex-col overflow-hidden"
        >
            <div className="flex-1 min-h-0 p-3">
                {!corporation?.maps_api_key && (
                    <div className="flex h-full items-center justify-center p-4 text-center text-sm text-[#AAAAAA]">
                        Chave do Google Maps não configurada.
                    </div>
                )}

                {corporation?.maps_api_key && places.length === 0 && (
                    <div className="flex h-full items-center justify-center p-4 text-center text-sm text-[#AAAAAA]">
                        Nenhum ponto de captura encontrado.
                    </div>
                )}

                {corporation?.maps_api_key && places.length > 0 && (
                    <APIProvider
                        apiKey={corporation.maps_api_key}
                        language="pt-BR"
                    >
                        <MapPassagesContent places={places}/>
                    </APIProvider>
                )}
            </div>
        </DashCard>
    );
}
